// (c) 2026 Owl Consultations. All rights reserved.
// Proprietary and confidential — licensed use only.
//
// Clinical Review (PCW) AI boundary — owner ruling 2026-10-31: the AI gets the patient's AGE in whole
// years (it needs it for accurate goals and dosing), never the date of birth. Until this file existed
// the §4 sub-domain prompts (4.2 pharmacokinetics, 4.7 risk) carried the DOB verbatim as ISO
// yyyy-MM-dd, and every PCW section called POST carerx/ai/assist directly, skipping the client-side
// de-identification the Plan of Treatment AI panel runs (aiClient.tokenizePHI). This is the one
// code path every PCW AI call now goes through:
//
//   buildPcwSubdomainPrompt  §4 prompts, verbatim asp-pot wording except "Patient age (years): N"
//   pcwAiAssist              tokenize the known identifiers → server (its own PHI scan + audit) → detokenize
//   ageYearsForAi            HIPAA Safe Harbor: ages over 89 are reported as "90 or older"
//
// The server's scan (CareRxAiFunctions.PhiPatterns) blocks a full date in any common format with
// 422 phi_not_allowed, so a regression here cannot reach the AI provider either.
import * as api from '../../../api/client'
import type { DrugMatrixEntry } from '../../../api/client'
import { formatUsDate } from '../../../api/format'
import { detokenize, tokenizePHI, type TokenMap } from '../../carerx/aiClient'
import type { PcwSec2, PcwSec3, PcwSubdomainKey } from '../potApi'
import type { PcwPatientInfo } from './pcwShared'

/** The identifiers the PCW editor holds for the patient; everything the tokenizer needs. */
export type PcwPhiSource = Pick<PcwPatientInfo, 'name' | 'mrn' | 'dob'>

const ISO_DATE = /^(\d{4})-(\d{2})-(\d{2})/
const US_DATE = /^(\d{1,2})[/-](\d{1,2})[/-](\d{4})/

/** yyyy-MM-dd (with or without a time part) or M/D/yyyy → calendar parts; anything else → null. */
export function parseDobParts(dob: string | null | undefined): { y: number; m: number; d: number } | null {
  if (!dob) return null
  const s = String(dob).trim()
  let y: number, m: number, d: number
  const iso = ISO_DATE.exec(s)
  if (iso) { y = Number(iso[1]); m = Number(iso[2]); d = Number(iso[3]) }
  else {
    const us = US_DATE.exec(s)
    if (!us) return null
    m = Number(us[1]); d = Number(us[2]); y = Number(us[3])
  }
  if (!(y >= 1800 && y <= 2200) || m < 1 || m > 12 || d < 1 || d > 31) return null
  return { y, m, d }
}

/**
 * Age in whole years as the AI may see it: "67", "90 or older" (HIPAA Safe Harbor §164.514(b)(2)(i)(C):
 * ages over 89 are aggregated), or "unknown" when the DOB is missing, unparseable or implausible.
 */
export function ageYearsForAi(dob: string | null | undefined, today: Date = new Date()): string {
  const p = parseDobParts(dob)
  if (!p) return 'unknown'
  let age = today.getFullYear() - p.y
  const month = today.getMonth() + 1
  if (month < p.m || (month === p.m && today.getDate() < p.d)) age--
  if (age < 0 || age > 130) return 'unknown'
  if (age > 89) return '90 or older'
  return String(age)
}

const pad2 = (n: number) => String(n).padStart(2, '0')

/**
 * The patient values that must never leave the browser in clear: the display name and each part of it
 * (asp-pot tokenizes ptFirst / ptLast separately), the MRN, and the DOB in every spelling the page or
 * the API may have produced (ISO, ISO with time, MM/DD/YYYY, M/D/YYYY, the US display format).
 */
export function pcwPhiValues(pt: PcwPhiSource): string[] {
  const out: string[] = []
  const name = (pt.name || '').trim()
  if (name && name !== '—') {
    out.push(name)
    for (const part of name.split(/[\s,]+/)) if (part.length >= 3) out.push(part)
  }
  if (pt.mrn) out.push(pt.mrn.trim())
  if (pt.dob) {
    out.push(String(pt.dob).trim())
    const p = parseDobParts(pt.dob)
    if (p) {
      out.push(`${p.y}-${pad2(p.m)}-${pad2(p.d)}`, `${pad2(p.m)}/${pad2(p.d)}/${p.y}`, `${p.m}/${p.d}/${p.y}`, `${pad2(p.m)}-${pad2(p.d)}-${p.y}`)
      const shown = formatUsDate(pt.dob)
      if (shown && shown !== '—') out.push(shown)
    }
  }
  return [...new Set(out.filter(v => v && v.trim().length >= 2))]
}

export type PcwAiMessage = { role: 'user' | 'assistant'; content: string }

/**
 * POST carerx/ai/assist for the PCW, through the same de-identification as the POT AI panel:
 * every turn is tokenized against the patient's identifiers before it leaves the browser and the
 * reply is detokenized for the pharmacist. The server still runs its own scan and audit row.
 */
export async function pcwAiAssist(pt: PcwPhiSource, messages: PcwAiMessage[], maxTokens?: number): Promise<{ text: string; phi_flag: string; phi_count: number; model?: string }> {
  return deidentifiedAiAssist(pcwPhiValues(pt), messages, maxTokens)
}

/** The same browser-side tokenize → server → detokenize path for any caller that knows the patient's identifiers. */
export async function deidentifiedAiAssist(phi: string[], messages: PcwAiMessage[], maxTokens?: number): Promise<{ text: string; phi_flag: string; phi_count: number; model?: string }> {
  let map: TokenMap = {}
  const safe = messages.map(m => {
    const t = tokenizePHI(m.content, phi, map)
    map = t.map
    return { role: m.role, content: t.text }
  })
  const r = await api.aiAssist(safe, maxTokens)
  return { ...r, text: detokenize(r.text ?? '', map) }
}

export interface PcwPromptInput {
  patient: PcwPatientInfo
  sec2: PcwSec2
  sec3: PcwSec3
  /** sec6 pregnancy status, falling back to the patient banner's, then 'Unknown'. */
  pregnancy: string
  drug: DrugMatrixEntry | null
  /** Injected by tests so the age is deterministic. */
  today?: Date
}

const PCW_PROMPT_BASE = 'You are a clinical pharmacist decision support assistant. Respond ONLY with a JSON array of findings. Each finding: {"severity":"major|moderate|minor|info","description":"...","recommendation":"...","citation":"..."}. No preamble, no markdown, just the JSON array.'

/** asp-pot buildPrompt for §4.1–4.7 — wording verbatim, except that the patient is described by age in years. */
export function buildPcwSubdomainPrompt(k: PcwSubdomainKey, input: PcwPromptInput): string | null {
  const { patient: pt, sec2, sec3, drug } = input
  const drugName = pt.medication || 'Unknown drug'
  const diagnosis = sec2.primaryDx || pt.primaryDx || ''
  const meds = sec3.medications.map(m => m.drug + (m.strength ? ' ' + m.strength : '')).join(', ') || 'None listed'
  const allergies = sec3.nkda ? 'NKDA' : sec3.allergies.map(a => a.allergen).join(', ') || 'None listed'
  const comorbidities = sec2.comorbidities || 'None listed'
  const pregnancy = input.pregnancy || 'Unknown'
  const age = ageYearsForAi(pt.dob, input.today)
  const cls = drug ? ` (class: ${drug.drugClass})` : ''
  const base = PCW_PROMPT_BASE
  if (k === '4.1') return base + `\n\nEvaluate indication appropriateness:\nDrug: ${drugName}\nDiagnosis: ${diagnosis} (ICD-10: ${sec2.icd10 || pt.icd10 || ''})\nPrior therapies: ${sec2.priorTherapy.map(t => `${t.drug} (${t.response})`).join(', ') || 'None'}\n\nIs this drug FDA-approved for this diagnosis? If off-label, what is the evidence? Is this an appropriate line of therapy?`
  if (k === '4.2') return base + `\n\nEvaluate pharmacokinetic fit:\nDrug: ${drugName}${drug?.dosingReference ? ` (${drug.dosingReference})` : ''}\nPatient age (years): ${age}\nDiagnosis: ${diagnosis}\n\nDoes the patient need dose adjustment? Consider renal and hepatic function. Note if baseline labs are missing.`
  if (k === '4.3') return base + `\n\nEvaluate pharmacodynamic considerations:\nDrug: ${drugName} (class: ${drug?.drugClass || 'unknown'})\nDiagnosis: ${diagnosis}\nSeverity: ${sec2.severity || 'Not assessed'}\n\nWhat is the expected response timeline? When should clinical improvement be assessed?`
  if (k === '4.4') return base + `\n\nScreen for drug-drug interactions:\nPrescribed drug: ${drugName}\nCurrent medications: ${meds}\nAllergies: ${allergies}\n\nIdentify all major and moderate drug-drug interactions. Include severity, mechanism, and clinical recommendation for each.`
  if (k === '4.5') return base + `\n\nEvaluate drug-disease interactions:\nDrug: ${drugName}${cls}\nComorbidities: ${comorbidities}\nBaseline screening: TB, HepB, JCV status from labs if available\n\nDoes any comorbidity contraindicate or complicate this drug? Evaluate each comorbidity against the drug. Include severity and whether the condition needs to be cleared, monitored, or is contraindicated.`
  if (k === '4.6') return base + `\n\nEvaluate contraindications and black box warnings:\nDrug: ${drugName}${cls}\nKnown black box warnings: ${drug ? drug.blackBoxWarnings.join(', ') : 'Unknown'}\nPatient allergies: ${allergies}\nComorbidities: ${comorbidities}\nPregnancy status: ${pregnancy}\n\nAre any absolute or relative contraindications present? Evaluate each black box warning against this patient. Include severity: not applicable, mitigated, or escalate.`
  if (k === '4.7') return base + `\n\nComposite risk factor assessment:\nDrug: ${drugName}${cls}\nPatient age (years): ${age}\nComorbidities: ${comorbidities}\nAllergies: ${allergies}\nPregnancy status: ${pregnancy}\n\nEvaluate risk in each category: infection risk, malignancy risk, cardiovascular risk, infusion reaction risk, immunogenicity, fall risk, pregnancy/lactation. Provide a severity rating (low/moderate/high) for each category with brief rationale.`
  return null
}
